import { Popover, Row, Col } from "antd";
import {
  find,
  isEmpty,
  isNil,
} from "lodash";
import React, { useEffect, useState } from "react";
import { CellRenderers } from "utils/tableUtils";
import { LoadingIcon } from "components/Icons";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { RISK_SCORE_DOC_LINK } from "utils/riskScoreUtil";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { BASE_GUTTER } from "constants/Layout";
import useResolvedFindingStatus from "hooks/useResolvedFindingStatus";

export default function FindingAssetScoreFormula({ finding }) {
  const { tagsUsedForAssetScore, assetScore } = finding || {};
  const [assetScoreByWeight, setAssetScoreByWeight] = useState(true)
  const [defaultWeight, setDefaultWeight] = useState();
  const [maxWeight, setMaxWeight] = useState();
  const [tagsArr, setTagsArr] = useState([]);
  const [loading, setLoading] = useState(true)
  const [totalWeight, setTotalWeight] = useState()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const featureFlags = useFeatureFlag();
  const resolvedStatus = useResolvedFindingStatus();
  useEffect(() => {
    if (!featureFlags.RISK_SCORE_3_0 || !isPopoverOpen) return;

    const { cancel: cancel1, promise: promise1 } = GlobalSettingAPI.getAssetWeightingScores();
    const { cancel: cancel2, promise: promise2 } = GlobalSettingAPI.getAssetScoreTags();
    const refArr = []
    //reset to true when all tags are deleted and page is refreshed
    setAssetScoreByWeight(true);
    setLoading(true)
    Promise.all([promise1, promise2]).then(([assetWeight, assetTags]) => {

      const defaultWeight = find(assetWeight, { field: "default-asset-weighting" }).fieldValue;
      const maxWeight = find(assetWeight, { field: "max-asset-weighting" }).fieldValue;

      setDefaultWeight(defaultWeight);
      setMaxWeight(maxWeight);

      if (!isEmpty(tagsUsedForAssetScore)) {
        tagsUsedForAssetScore.forEach((tag) => {
          const [tagName] = tag.split(':');

          const matchingAssets = assetTags.filter(asset => {
            const assetTag = isEmpty(asset.fieldValue) ? asset.name : `${asset.name}:${asset.fieldValue}`;
            // exactValue=true: match full tag exactly
            // exactValue=false: match by key name only
            return (asset.exactValue || asset.fieldValue) ? assetTag === tag : asset.name === tagName;
          });

          matchingAssets.forEach(matchingAsset => {
            refArr.push({
              tag,
              value: matchingAsset.value
            });
          });
        })
      };

      if (!isEmpty(refArr)) {
        const total = refArr.map(tag => tag?.value).reduce((a, c) => a + c, 0);
        setAssetScoreByWeight(false);
        setTagsArr(refArr);
        setTotalWeight(total);
      }
    }).finally(() => {
      setLoading(false);
    });
    return () => {
      cancel1();
      cancel2();
    }
  }, [tagsUsedForAssetScore, featureFlags.RISK_SCORE_3_0, finding?.tagsMetaMap, isPopoverOpen]);

  const calculatedAssetScore = assetScoreByWeight ? (defaultWeight * 10) / maxWeight : (totalWeight * 10) / maxWeight;

  // If the finding is resolved i.e Mitigated, remediated, false+ve, accept risk or severity is INFO, we should not show any calculation logic tooltip, the scores may not match with the current state of the finding
  const isStatusResolvedOrInfoSeverity = resolvedStatus.includes(finding?.status) || finding?.severity === "INFO";

  if (isStatusResolvedOrInfoSeverity || !featureFlags.RISK_SCORE_3_0 || isNil(assetScore)) {
    return CellRenderers.VALUE_OR_NA(assetScore);
  }

  const RenderByWeight = () => (
    <>
      <Row gutter={BASE_GUTTER}>
        <Col span={5} className="d-flex justify-content-end align-items-center">
          <div>
            <strong>{assetScore}</strong> {calculatedAssetScore > 10 ? <>({calculatedAssetScore.toFixed(0)})</> : null} =
          </div>
        </Col>
        <Col span={19} className="d-flex justify-content-start">
          <div className="d-flex flex-column align-items-center justify-content-center">
            <div>Default Asset weighting <strong>({defaultWeight})</strong> x <strong>10</strong></div>
            <div style={{ borderTop: '1px solid #d9d9d9' }}>Maximum Total Asset weighting <strong>({maxWeight})</strong></div>
          </div>
        </Col>
      </Row>
    </>
  )

  const RenderByTags = () => (
    <>
      <Row gutter={BASE_GUTTER}>
        <Col span={calculatedAssetScore<=0?3:5} className="d-flex justify-content-end align-items-center">
          <div>
            <strong>{assetScore}</strong> {calculatedAssetScore > 10 ? <>({calculatedAssetScore.toFixed(0)})</> : null} =
          </div>
        </Col>
        <Col span={calculatedAssetScore<=0?17.5:19} className="d-flex justify-content-start">
          <div className="d-flex flex-column align-items-center justify-content-center">
            <div>∑ tags <strong>({totalWeight})</strong> x <strong>10</strong></div>
            <div style={{ borderTop: '1px solid #d9d9d9' }}>Maximum Total Asset weighting <strong>({maxWeight})</strong></div>
          </div>
        </Col>
        {calculatedAssetScore<=0 && <Col span={3.5} className="d-flex justify-content-start align-items-center">
          <div>
             =<strong>{Math.ceil((totalWeight*10)/maxWeight)}</strong> (min:1)
          </div>
        </Col>}
      </Row>

      <Row gutter={BASE_GUTTER} style={{fontSize: 11 , marginTop: 6 }} >
        <Col span={5} className="d-flex justify-content-end align-items-center">
          <div>
            Tags({totalWeight}) :
          </div>
        </Col>
        <Col span={19} className="d-flex justify-content-flex-start">
          <div>{tagsArr.map((t, i) => <span key={i}>{t.tag}({t.value}){i !== tagsArr.length - 1 ? ', ' : ''}</span>)}</div>
        </Col>
      </Row>
    </>
  )

  const content = loading ? (
    <LoadingIcon />
  ) :
    <div style={{ width: 390 }}>
      {
        assetScoreByWeight
          ?
          <RenderByWeight />
          :
          <RenderByTags />
      }

      <div className="p-t-xs text-right tag-tooltip-doc-link" >
        {calculatedAssetScore > 10 ? <span className="text-left m-r-l">Scores will be capped at 10</span> : null}
        {RISK_SCORE_DOC_LINK}</div>
    </div>

  return (
    <Popover align={{ offset: [0, 6]}} content={content} onVisibleChange={()=>setIsPopoverOpen(prevVal => !prevVal)}>
      <div style={{width:"max-content"}}>{assetScore}</div>
    </Popover>
  );
}

export function prioritizeTags(refArr, tags) {
  const result = [];
  for (const { tag, value } of refArr) {
    const foundInManual = tags["FINDING"]?.includes(tag);
    const foundInSubProduct = tags["SUBPRODUCT"]?.includes(tag);

    if (foundInManual) {
      if (tags["PRODUCT"] && tags["PRODUCT"]?.includes(tag) && !tags["FINDING"]?.includes(tag)) {
        // Exclude conflicting tag
        continue;
      }
      if (tags["SUBPRODUCT"] && tags["SUBPRODUCT"]?.includes(tag) && !tags["FINDING"]?.includes(tag)) {
        // Exclude conflicting tag
        continue;
      }
    }

    if (foundInSubProduct) {
      if (tags["PRODUCT"] && tags["PRODUCT"]?.includes(tag) && (!tags["FINDING"] || !tags["FINDING"]?.includes(tag))) {
        // Exclude conflicting tag
        continue;
      }
    }
    result.push({ tag, value });
  }

  return result;
}




